import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ExpiringBanner from './ExpiringBanner';

const LESSONS = [
  {
    stage: '1단계 — 직업 이해 & 진입',
    badge: '입문',
    items: [
      { num: '1-1', title: '이러닝이란 무엇인가' },
      { num: '1-2', title: '교수설계자 역할과 플로우' },
      { num: '1-3', title: '이러닝 강의 유형 이해' },
      { num: '1-4', title: '필요 역량과 자기 진단' },
      { num: '1-5', title: '일 구하는 법 · 시장 탐색' },
      { num: '1-6', title: '실무 도구 & 원고 체크리스트' },
    ],
  },
  {
    stage: '2단계 — 원고 분석 & 가공',
    badge: '기초',
    items: [
      { num: '2-1', title: '지식의 뼈대 추출' },
      { num: '2-2', title: '모듈형 청킹 설계' },
      { num: '2-3', title: '고밀도 요약 기술' },
      { num: '2-4', title: '시각적 모듈화 설계' },
      { num: '2-5', title: 'SME 원고 유형별 CPR' },
      { num: '2-6', title: '원고 최종 감정 & 품질 기준' },
    ],
  },
  {
    stage: '3단계 — 스토리보드 설계 & 실전',
    badge: '실무',
    items: [
      { num: '3-1', title: '스토리보드 정체성 & 첫 설계' },
      { num: '3-2', title: '타겟 분석 & 목표 설정' },
      { num: '3-3', title: '학습 논리 모델링' },
      { num: '3-4', title: '화면 배치 & 시각적 위계' },
      { num: '3-5', title: '유형별 화면 구조화 전략' },
      { num: '3-6', title: '나레이션 대본 작성' },
      { num: '3-7', title: '검수 · SME 협업 · 자산화' },
    ],
  },
];

const STAGE_COLORS = {
  입문: { bg: '#EBF1FA', color: '#0C447C' },
  기초: { bg: '#EBF5EF', color: '#27500A' },
  실무: { bg: '#FDF3E7', color: '#633806' },
};

export default function CoursePage() {
  useAuth();
  const navigate = useNavigate();

  const handleLessonClick = (num) => {
    navigate(`/lesson/${num}`);
  };

  return (
    <div style={s.page}>
      <ExpiringBanner />

      <div style={s.hero}>
        <div style={s.heroInner}>
          <div style={s.heroTitle}>교수설계자 완성 과정</div>
          <div style={s.heroDesc}>
            이러닝 산업의 전체 그림부터 실제 스토리보드 납품까지,
            <br />
            19개 레슨으로 완성합니다.
          </div>
          <div style={s.heroMeta}>
            <span>📚 총 19개 레슨</span>
            <span>⏱ 약 8~10시간</span>
            <span>🎯 입문 → 실무</span>
          </div>
        </div>
      </div>

      <main style={s.main}>
        {LESSONS.map(({ stage, badge, items }) => {
          const col = STAGE_COLORS[badge];

          return (
            <section key={stage} style={s.section}>
              <div style={s.stageHeader}>
                <span
                  style={{
                    ...s.stageBadge,
                    background: col.bg,
                    color: col.color,
                  }}
                >
                  {badge}
                </span>

                <span style={s.stageTitle}>{stage}</span>
                <span style={s.stageCount}>{items.length}개 레슨</span>
              </div>

              <div style={s.lessonGrid}>
                {items.map(({ num, title }) => (
                  <button
                    key={num}
                    type="button"
                    style={s.lessonCard}
                    onClick={() => handleLessonClick(num)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1B4A8C';
                      e.currentTarget.style.background = '#EBF1FA';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#E2E0DB';
                      e.currentTarget.style.background = '#FFFFFF';
                    }}
                  >
                    <span style={s.lessonNum}>{num}</span>

                    <div style={s.lessonInfo}>
                      <div style={s.lessonTitle}>{title}</div>
                      <div style={s.lessonLevel}>{badge}</div>
                    </div>

                    <span style={s.lessonArrow}>→</span>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
      </main>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#F7F6F4',
    fontFamily: "'Noto Sans KR', sans-serif",
  },

  hero: {
    background: '#1B4A8C',
    padding: '48px 32px 44px',
    color: '#fff',
  },

  heroInner: {
    maxWidth: '760px',
    margin: '0 auto',
  },

  heroTitle: {
    fontSize: '24px',
    fontWeight: '500',
    marginBottom: '10px',
  },

  heroDesc: {
    fontSize: '15px',
    opacity: 0.8,
    lineHeight: '1.65',
    marginBottom: '20px',
  },

  heroMeta: {
    display: 'flex',
    gap: '20px',
    fontSize: '13px',
    opacity: 0.7,
    flexWrap: 'wrap',
  },

  main: {
    maxWidth: '860px',
    margin: '0 auto',
    padding: '36px 24px 80px',
  },

  section: {
    marginBottom: '36px',
  },

  stageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '14px',
    flexWrap: 'wrap',
  },

  stageBadge: {
    fontSize: '11px',
    fontWeight: '500',
    padding: '4px 10px',
    borderRadius: '4px',
  },

  stageTitle: {
    fontSize: '15px',
    fontWeight: '500',
    color: '#1A1A2E',
  },

  stageCount: {
    fontSize: '12px',
    color: '#9898AA',
    marginLeft: 'auto',
  },

  lessonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
  },

  lessonCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#FFFFFF',
    border: '0.5px solid #E2E0DB',
    borderRadius: '10px',
    padding: '13px 16px',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
    minHeight: '60px',
  },

  lessonNum: {
    fontSize: '11px',
    fontWeight: '500',
    color: '#9898AA',
    flexShrink: 0,
    width: '22px',
  },

  lessonInfo: {
    flex: 1,
    minWidth: 0,
  },

  lessonTitle: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#1A1A2E',
    lineHeight: '1.35',
    marginBottom: '2px',
  },

  lessonLevel: {
    fontSize: '11px',
    color: '#9898AA',
  },

  lessonArrow: {
    fontSize: '11px',
    color: '#9898AA',
    flexShrink: 0,
  },
};